import { useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import {
  Users,
  Maximize2,
  BedDouble,
  Bath,
  Wifi,
  Wind,
  Tv,
  Wine,
  ShieldCheck,
  Waves,
  UtensilsCrossed,
  LayoutList,
  Mountain,
  ArrowLeft,
  Calendar,
  Search,
} from "lucide-react";
import SEO from "@/components/shared/SEO";
import RoomGallery from "@/features/rooms/components/RoomGallery";
import {
  usePublicRoom,
  usePublicRooms,
} from "@/features/rooms/hooks/usePublicRooms";
import RoomCard from "@/components/shared/RoomCard";
import Button from "@/components/ui/Button";
import { formatCurrency, formatArea } from "@/lib/utils/formatters";
import { ROOM_AMENITIES } from "@/types/rooms";
import type { RoomAmenityKey } from "@/types/rooms";

const AMENITY_ICONS: Record<RoomAmenityKey, React.ElementType> = {
  wifi: Wifi,
  ac: Wind,
  tv: Tv,
  minibar: Wine,
  safe: ShieldCheck,
  hairdryer: Wind,
  bathtub: Waves,
  kitchenette: UtensilsCrossed,
  diningArea: LayoutList,
  balcony: Mountain,
  lakeView: Mountain,
};

const fmt = (d: Date) => d.toISOString().split("T")[0];

function BookingWidget({ basePrice }: { basePrice: number }) {
  const navigate = useNavigate();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const [checkIn, setCheckIn] = useState(fmt(today));
  const [checkOut, setCheckOut] = useState(fmt(tomorrow));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  const nights =
    checkIn && checkOut
      ? Math.max(
          0,
          Math.round(
            (new Date(checkOut).getTime() - new Date(checkIn).getTime()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : 1;

  const fieldBase =
    "flex flex-col gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition focus-within:border-metro-green-800 focus-within:ring-2 focus-within:ring-metro-green-800/10";

  return (
    <div className="overflow-hidden rounded-2xl border border-metro-cream-100 shadow-card">
      <div className="bg-metro-green-950 px-6 py-5 text-white">
        <p className="mb-0.5 text-[10px] font-medium uppercase tracking-widest text-white/50">
          Starting from
        </p>
        <p className="font-serif text-3xl">{formatCurrency(basePrice)}</p>
        <p className="text-sm text-white/60">per night · taxes extra</p>
      </div>
      <div className="bg-metro-cream p-5 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
          Check availability
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div className={fieldBase}>
            <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle">
              <Calendar size={10} />
              Check in
            </label>
            <input
              type="date"
              value={checkIn}
              min={fmt(today)}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (checkOut <= e.target.value) {
                  const n = new Date(e.target.value);
                  n.setDate(n.getDate() + 1);
                  setCheckOut(fmt(n));
                }
              }}
              className="w-full bg-transparent text-sm font-medium text-metro-green-950 outline-none"
            />
          </div>
          <div className={fieldBase}>
            <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle">
              <Calendar size={10} />
              Check out
            </label>
            <input
              type="date"
              value={checkOut}
              min={checkIn}
              onChange={(e) => setCheckOut(e.target.value)}
              className="w-full bg-transparent text-sm font-medium text-metro-green-950 outline-none"
            />
          </div>
        </div>
        <div className={fieldBase}>
          <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-metro-ink-subtle">
            <Users size={10} />
            Guests
          </label>
          <div className="flex flex-wrap gap-4">
            {[
              { label: "Adults", val: adults, set: setAdults, min: 1, max: 10 },
              {
                label: "Children",
                val: children,
                set: setChildren,
                min: 0,
                max: 6,
              },
            ].map(({ label, val, set, min, max }) => (
              <div key={label} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => set((v) => Math.max(min, v - 1))}
                  className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 text-sm text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
                >
                  −
                </button>
                <span className="w-4 text-center text-sm font-medium text-metro-green-950">
                  {val}
                </span>
                <button
                  type="button"
                  onClick={() => set((v) => Math.min(max, v + 1))}
                  className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 text-sm text-metro-ink-muted transition hover:border-metro-green-800 hover:text-metro-green-800"
                >
                  +
                </button>
                <span className="text-xs text-metro-ink-subtle">{label}</span>
              </div>
            ))}
          </div>
        </div>
        {nights > 0 && (
          <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm">
            <span className="text-metro-ink-muted">
              {formatCurrency(basePrice)} × {nights}{" "}
              {nights === 1 ? "night" : "nights"}
            </span>
            <span className="font-semibold text-metro-green-950">
              {formatCurrency(basePrice * nights)}
            </span>
          </div>
        )}
        <Button
          fullWidth
          leftIcon={<Search size={15} />}
          onClick={() =>
            navigate(
              `/book?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}`,
            )
          }
        >
          Check Availability
        </Button>
      </div>
      <div className="border-t border-metro-cream-100 bg-white px-5 py-4 space-y-1.5 text-xs text-metro-ink-subtle">
        <p>✓ Free cancellation up to 48 hours before check-in</p>
        <p>✓ No prepayment required</p>
        <p>✓ Best rate guaranteed when booked direct</p>
      </div>
    </div>
  );
}

export default function RoomDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: room, isLoading, isError } = usePublicRoom(slug ?? "");
  const { data: allRooms = [] } = usePublicRooms();

  if (isLoading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-metro-green-100 border-t-metro-green-800" />
      </div>
    );

  if (isError || !room) return <Navigate to="/rooms" replace />;

  const otherRooms = allRooms.filter((r) => r.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={room.name}
        description={room.shortDescription}
        image={room.images?.[0]}
      />
      <div className="bg-white">
        <div className="bg-metro-cream pt-10 pb-8">
          <div className="container-narrow">
            <Link
              to="/rooms"
              className="mb-6 inline-flex items-center gap-1.5 text-sm text-metro-ink-subtle transition hover:text-metro-green-800"
            >
              <ArrowLeft size={14} />
              All Rooms
            </Link>
            <p className="eyebrow mb-2">Accommodation</p>
            <h1 className="mb-2 font-serif text-display-md text-metro-green-950">
              {room.name}
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-metro-ink-muted">
              {room.shortDescription}
            </p>
          </div>
        </div>

        <div className="container-narrow py-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_340px]">
            <div className="min-w-0">
              <div className="mb-8">
                <RoomGallery images={room.images ?? []} alt={room.name} />
              </div>

              {/* Stats */}
              <div className="mb-10 grid grid-cols-2 gap-3 rounded-2xl bg-metro-cream p-5 sm:grid-cols-4">
                {[
                  {
                    icon: Maximize2,
                    value: formatArea(room.size),
                    label: "Room size",
                  },
                  {
                    icon: Users,
                    value: String(room.maxGuests),
                    label: "Max guests",
                  },
                  {
                    icon: BedDouble,
                    value: room.bedConfig,
                    label: "Bed config",
                  },
                  {
                    icon: Bath,
                    value: String(room.bathrooms),
                    label: room.bathrooms === 1 ? "Bathroom" : "Bathrooms",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="flex flex-col items-center gap-1.5 text-center"
                  >
                    <s.icon size={20} className="text-metro-green-800" />
                    <span className="font-medium text-metro-green-950 text-sm break-words leading-tight">
                      {s.value}
                    </span>
                    <span className="text-xs text-metro-ink-subtle">
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mb-10">
                <h2 className="mb-4 font-serif text-2xl text-metro-green-950">
                  About this room
                </h2>
                <p className="leading-relaxed text-metro-ink-muted">
                  {room.description}
                </p>
              </div>

              {room.amenities?.length > 0 && (
                <div className="mb-10">
                  <h2 className="mb-5 font-serif text-2xl text-metro-green-950">
                    Amenities
                  </h2>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {room.amenities.map((key) => {
                      const meta = ROOM_AMENITIES.find((a) => a.key === key);
                      if (!meta) return null;
                      const Icon = AMENITY_ICONS[key as RoomAmenityKey] ?? Wifi;
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-2.5 rounded-lg border border-metro-cream-100 px-4 py-3 text-sm text-metro-ink-muted"
                        >
                          <Icon
                            size={16}
                            className="shrink-0 text-metro-green-800"
                          />
                          {meta.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mb-10 lg:hidden">
                <h2 className="mb-4 font-serif text-2xl text-metro-green-950">
                  Book This Room
                </h2>
                <BookingWidget basePrice={room.basePrice} />
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="sticky top-24 space-y-4">
                <BookingWidget basePrice={room.basePrice} />
                <div className="rounded-xl bg-metro-cream p-4 text-center text-sm text-metro-ink-muted">
                  Need help?{" "}
                  <a
                    href="tel:+919906510627"
                    className="font-medium text-metro-green-800 hover:underline"
                  >
                    Call us
                  </a>{" "}
                  or{" "}
                  <Link
                    to="/contact"
                    className="font-medium text-metro-green-800 hover:underline"
                  >
                    send a message
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {otherRooms.length > 0 && (
            <div className="mt-20 border-t border-metro-cream-100 pt-16">
              <h2 className="mb-8 font-serif text-display-md text-metro-green-950">
                Other Rooms
              </h2>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {otherRooms.map((r) => (
                  <RoomCard key={r.id} room={r} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}